import * as React from 'react';

import { IOwner, IPet, IPetType, IVisit, IError, IRouterContext } from '../../types';

import { url, submitForm, safeJsonParse, createFetchOptions } from '../../util';
import { NotEmpty } from '../form/Constraints';

import DateInput from '../form/DateInput';
import Input from '../form/Input';
import PetDetails from './PetDetails';
import BackButton from '../common/BackButton';


interface IVisitsPageProps {
  params: {
    ownerId: string,
    petId: string
  };
}

interface IVisitsPageState {
  visit?: IVisit;
  owner?: IOwner;
  error?: IError;
}

export default class VisitsPage extends React.Component<IVisitsPageProps, IVisitsPageState> {

 context: IRouterContext;
 private _isMounted: boolean = false;

  static contextTypes = {
    router: React.PropTypes.object.isRequired
  };


  constructor(props: IVisitsPageProps) {
    super(props);

    this.onInputChange = this.onInputChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    
    // Initialize visit object to prevent undefined errors
    this.state = {
      visit: { id: null, isNew: true, date: null, description: '' }
    };
  }

  componentDidMount() {
    this._isMounted = true;
    const { params } = this.props;

    if (params && params.ownerId) {
      fetch(url(`/api/owners/${params.ownerId}`), createFetchOptions())
        .then(response => safeJsonParse(response))
        .then(owner => {
          if (this._isMounted) {
            this.setState(
              {
                owner: owner,
                visit: { id: null, isNew: true, date: null, description: '' }
              });
          }
        })
        .catch(error => {
          console.error('Error loading owner:', error);
        });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const petId = this.props.params.petId;
    const { owner, visit } = this.state;

    // Ensure visit exists before accessing properties
    if (!visit || !owner) {
      console.error('Cannot submit: visit or owner is missing', { visit, owner });
      return;
    }

    const request = {
      date: visit.date || null,
      description: visit.description || ''
    };

    const url = '/api/owners/' + owner.id + '/pets/' + petId + '/visits';
    submitForm('POST', url, request, (status, response) => {
      if (!this._isMounted) {
        return;
      }

      // Backend returns 201 CREATED with the visit object, not 204
      if (status === 200 || status === 201 || status === 204) {
        // Visit created successfully, navigate to owner details page
        const successMessage = 'Visit added successfully!';
        setTimeout(() => {
          window.location.href = `/owners/${owner.id}?success=${encodeURIComponent(successMessage)}`;
        }, 100);
      } else {
        console.log('ERROR?!...', response);
        if (this._isMounted) {
          // CRITICAL: Preserve visit when setting error state
          // This prevents visit from becoming undefined during error handling
          const currentVisit = this.state.visit;
          if (!currentVisit || typeof currentVisit !== 'object') {
            // If visit is invalid, restore it with default values
            const fallbackVisit = { id: null, isNew: true, date: null, description: '' };
            this.setState({ 
              error: response,
              visit: fallbackVisit
            });
          } else {
            // visit is valid, just set the error
            this.setState({ error: response });
          }
        }
      }
    });
  }

  onInputChange(name: string, value: string) {
    const { visit } = this.state;
    
    // Ensure visit exists before modifying
    if (!visit || typeof visit !== 'object') {
      console.error('onInputChange: visit is invalid', visit);
      return;
    }

    const updatedVisit = Object.assign({}, visit, { [name]: value });
    this.setState({ visit: updatedVisit });
  }

  render() {
    if (!this.state) {
      return <h2>Loading...</h2>;
    }

    const { owner, error, visit } = this.state;
    const petId = this.props.params.petId;

    // Don't render until owner is loaded
    if (!owner) {
      return <h2>Loading...</h2>;
    }

    // CRITICAL: Ensure visit is always a valid object
    // Create a default visit object if visit is undefined/null/invalid
    let safeVisit: any = null;
    if (visit && typeof visit === 'object' && !Array.isArray(visit)) {
      // visit is valid, use it
      safeVisit = {
        id: visit.id !== undefined ? visit.id : null,
        isNew: visit.isNew !== undefined ? visit.isNew : true,
        date: visit.date !== undefined ? visit.date : null,
        description: visit.description !== undefined ? visit.description : ''
      };
    } else {
      // visit is invalid, create default
      safeVisit = { id: null, isNew: true, date: null, description: '' };
    }
    
    // Final validation - ensure safeVisit is a valid object
    if (!safeVisit || typeof safeVisit !== 'object' || Array.isArray(safeVisit)) {
      console.error('VisitsPage: Failed to create safeVisit', { visit, safeVisit });
      return <h2>Error: Invalid visit data. Please refresh the page.</h2>;
    }

    const pet = owner.pets && owner.pets.find ? owner.pets.find(candidate => candidate.id.toString() === petId) : null;

    return (
      <div>
        <BackButton />
        <h2>Visits</h2>
        <b>Pet</b>
        {pet && <PetDetails owner={owner} pet={pet} />}

        <form className='form-horizontal' method='POST' action={url('/api/owner')}>
          <div className='form-group has-feedback'>
            {/* safeVisit is guaranteed to be a valid object at this point */}
            <DateInput object={safeVisit} error={error} label='Date' name='date' onChange={this.onInputChange} />
            <Input object={safeVisit} error={error} constraint={NotEmpty} label='Description' name='description' onChange={this.onInputChange} />
          </div>
          <div className='form-group'>
            <div className='col-sm-offset-2 col-sm-10'>
              <button className='btn btn-default' type='button' onClick={this.onSubmit}>Add Visit</button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}


